import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useDecideProgressRunMutation,
  useGetProgressRunQuery,
  useLazyExportProgressRunQuery,
} from '@shared/api';
import type { ProgressDeviation, ProgressKpi, ProgressRunDecision } from '@entities/progress';
import {
  apiErrorMessage,
  decisionLabel,
  decisionTone,
  deviationSeverityLabel,
  deviationSeverityTone,
  fmtDateTime,
  fmtDelta,
  fmtNum,
  fmtPct,
  isProgressRunActive,
  runObjectLabel,
  runStageLabel,
  statusLabel,
  statusTone,
  useProgressPermission,
} from '@entities/progress';
import { riseIn } from '@shared/theme/animations';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Progress } from '@shared/ui/Progress';
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
} from '@shared/ui/icons';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  width: max-content;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/* Шапка-отчёт: лёгкий «бумажный» градиент, чтобы выделить заголовок документа. */
const HeaderCard = styled(Card)`
  background: ${({ theme }) => theme.colors.gradient.surface};
`;

const HeadRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const HeadLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.colors.primary};
`;

const RunTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const SubMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-wrap: wrap;
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const ProgressBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const ProgressTopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ProgressStage = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProgressPercent = styled.span`
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
  flex-shrink: 0;
`;

const ErrBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.danger}12;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

/* ── Лента headline-KPI (освоение) ──────────────────────────────────────── */

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const StatCard = styled.div<{ $review: boolean; $index: number }>`
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid
    ${({ theme, $review }) => ($review ? `${theme.colors.status.warning}55` : theme.colors.border)};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  animation: ${riseIn} 380ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $index }) => `${$index * 70}ms`};
`;

const StatTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const StatPct = styled.div`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1;
`;

const StatFoot = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-variant-numeric: tabular-nums;
`;

/* ── Таблица показателей ────────────────────────────────────────────────── */

const SectionTitle = styled.h3`
  margin: ${({ theme }) => theme.spacing(1)} 0 ${({ theme }) => theme.spacing(3)};
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const GroupCaption = styled.h4`
  margin: ${({ theme }) => theme.spacing(5)} 0 ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:first-of-type {
    margin-top: 0;
  }
`;

/** Горизонтальный скролл широкой таблицы на узких экранах (≤360px) — страница
 *  не «уезжает», скроллится только таблица. */
const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.surface};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

/** Строка показателя. Требующие проверки подсвечиваем мягким фоном — не только
 *  бейджем (низкий контраст плохо читается при нарушениях цветовосприятия). */
const TrReview = styled.tr<{ $review: boolean }>`
  background: ${({ $review, theme }) => ($review ? theme.colors.primarySoft : 'transparent')};
`;

const KpiName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const KpiNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const ExpandButton = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(1.5)}`};
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0 -${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  svg {
    flex-shrink: 0;
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }
`;

const TraceCell = styled.td`
  background: ${({ theme }) => theme.colors.background};
`;

const TraceList = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin: 0;
`;

const TraceLabel = styled.dt`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TraceValue = styled.dd`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/* ── Пояснительная записка / предупреждения ─────────────────────────────── */

const NarrativeCard = styled(Card)`
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
`;

const Narrative = styled.div`
  font-size: 14px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const ReviewBar = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.gradient.surface};
`;

const ReviewText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const WarningList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const WarningItem = styled.li`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: flex-start;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => `${theme.colors.status.warning}0F`};
  border: 1px solid ${({ theme }) => `${theme.colors.status.warning}33`};
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/* ── Отклонения (Ф-3) ───────────────────────────────────────────────────── */

const DeviationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const DeviationCard = styled.div<{ $severity: string; $index: number }>`
  position: relative;
  padding: ${({ theme }) => `${theme.spacing(3.5)} ${theme.spacing(4)}`};
  padding-left: ${({ theme }) => theme.spacing(5)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  animation: ${riseIn} 360ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $index }) => `${$index * 60}ms`};

  /* Корешок по важности отклонения. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: ${({ theme }) => `${theme.radius.md} 0 0 ${theme.radius.md}`};
    background: ${({ theme, $severity }) =>
      $severity === 'critical'
        ? theme.colors.status.danger
        : $severity === 'major'
          ? theme.colors.status.warning
          : theme.colors.status.info};
  }
`;

const DevTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const DevTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DevMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const DevExplanation = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DevExplanationMuted = styled(DevExplanation)`
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const DevHypothesis = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Метрика отклонения для отображения: «−36%» (delta_pct) / «2 шт» (count). */
function deviationMetric(d: ProgressDeviation): string {
  if (d.metric === null || d.metric === undefined) return '';
  if (d.unit === '%') return fmtPct(d.metric);
  if (d.unit) return `${fmtNum(d.metric)} ${d.unit}`;
  return fmtNum(d.metric);
}

/** Блок «Отклонения» — отклонения посчитаны кодом, пояснение — гипотеза LLM
 *  (всегда совещательная, проходит HITL). */
function DeviationsSection({ deviations }: { deviations: ProgressDeviation[] }) {
  return (
    <DeviationList>
      {deviations.map((d, i) => {
        const metric = deviationMetric(d);
        const hasSignals = d.related_signals.length > 0;
        return (
          <DeviationCard key={d.code} $severity={d.severity} $index={i}>
            <DevTop>
              <DevTitle>{d.title}</DevTitle>
              <Badge $tone={deviationSeverityTone(d.severity)}>
                {deviationSeverityLabel(d.severity)}
              </Badge>
            </DevTop>
            {(metric || hasSignals) && (
              <DevMeta>
                {metric && <span>Отклонение: {metric}</span>}
                {metric && hasSignals && <span> · </span>}
                {hasSignals && <span>сопутствующие сигналы: {d.related_signals.join(', ')}</span>}
              </DevMeta>
            )}
            {d.explanation?.trim() ? (
              <DevExplanation>{d.explanation}</DevExplanation>
            ) : (
              <DevExplanationMuted>
                Пояснение не сформировано — причина требует анализа.
              </DevExplanationMuted>
            )}
            <DevHypothesis>
              <Badge $tone="neutral">гипотеза</Badge>
              требует проверки руководителем
            </DevHypothesis>
          </DeviationCard>
        );
      })}
    </DeviationList>
  );
}

/** Дружелюбная подпись группы показателей (зеркало DOCX-рендера). */
function groupLabel(group: string): string {
  switch (group) {
    case 'pto':
      return 'ПТО — физобъёмы и исполнительная документация';
    case 'smeta':
      return 'Сметы — освоение';
    case 'field':
      return 'Поле — сменная отчётность';
    case 'resources':
      return 'Ресурсы — техника и персонал';
    case 'meetings':
      return 'Совещания — поручения';
    case 'mto':
      return 'Снабжение (МТО) — поставки и заказы';
    case 'logist':
      return 'Логистика — рейсы и заявки';
    case 'geodesy':
      return 'Геодезия — исполнительные схемы';
    case 'requests':
      return 'Межотдельские заявки';
    default:
      return group;
  }
}

/** Отображаемое «факт»-значение: фактический показатель, либо счётчик (для
 *  KPI-количеств без плана/факта), либо прочерк. */
function factDisplay(kpi: ProgressKpi): string {
  if (kpi.value_fact !== null && kpi.value_fact !== undefined) return fmtNum(kpi.value_fact);
  if (kpi.count !== null && kpi.count !== undefined) return fmtNum(kpi.count);
  return '—';
}

/** Единица измерения показателя: явная единица, либо «шт» для счётчиков. */
function unitDisplay(kpi: ProgressKpi): string {
  if (kpi.unit) return kpi.unit;
  if (kpi.count !== null && kpi.count !== undefined) return 'шт';
  return '—';
}

/** KPI сгруппированы по `group` с сохранением порядка первого появления. */
function groupKpis(kpis: ProgressKpi[]): Array<[string, ProgressKpi[]]> {
  const map = new Map<string, ProgressKpi[]>();
  for (const k of kpis) {
    const arr = map.get(k.group);
    if (arr) arr.push(k);
    else map.set(k.group, [k]);
  }
  return Array.from(map.entries());
}

/** Лента ключевого освоения: показатели с готовым процентом освоения. */
function HeadlineBand({ kpis }: { kpis: ProgressKpi[] }) {
  const headline = kpis.filter((k) => k.completion_pct !== null && k.completion_pct !== undefined);
  if (headline.length === 0) return null;
  return (
    <StatGrid>
      {headline.map((k, i) => {
        const unit = k.unit ? ` ${k.unit}` : '';
        return (
          <StatCard key={k.code} $review={k.needs_review} $index={i}>
            <StatTitle>
              <span>{k.title}</span>
              {k.needs_review && <Badge $tone="warning">проверка</Badge>}
            </StatTitle>
            <StatPct>{fmtNum(k.completion_pct)}%</StatPct>
            <Progress percent={k.completion_pct ?? 0} />
            <StatFoot>
              факт {factDisplay(k)} из {fmtNum(k.value_plan)}
              {unit}
            </StatFoot>
          </StatCard>
        );
      })}
    </StatGrid>
  );
}

/** Одна строка показателя + раскрывающаяся прослеживаемость (источник, входные
 *  данные, пояснение к расчёту). */
function KpiRow({ kpi }: { kpi: ProgressKpi }) {
  const [open, setOpen] = useState(false);
  const hasTrace = kpi.inputs.length > 0 || Boolean(kpi.source_ref) || Boolean(kpi.formula_note);

  return (
    <>
      <TrReview $review={kpi.needs_review}>
        <td>
          <KpiName>{kpi.title}</KpiName>
          {kpi.note && <KpiNote>{kpi.note}</KpiNote>}
          {hasTrace && (
            <ExpandButton
              type="button"
              $open={open}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              Источник и расчёт
            </ExpandButton>
          )}
        </td>
        <td className="num">{fmtNum(kpi.value_plan)}</td>
        <td className="num">{factDisplay(kpi)}</td>
        <td className="num">{fmtDelta(kpi.delta)}</td>
        <td className="num">{fmtPct(kpi.delta_pct)}</td>
        <td>{unitDisplay(kpi)}</td>
        <td>{kpi.needs_review && <Badge $tone="warning">требует проверки</Badge>}</td>
      </TrReview>
      {open && hasTrace && (
        <tr>
          <TraceCell colSpan={7}>
            <TraceList>
              {kpi.formula_note && (
                <>
                  <TraceLabel>Расчёт</TraceLabel>
                  <TraceValue>{kpi.formula_note}</TraceValue>
                </>
              )}
              {kpi.source_ref && (
                <>
                  <TraceLabel>Источник</TraceLabel>
                  <TraceValue>{kpi.source_ref}</TraceValue>
                </>
              )}
              {kpi.inputs.length > 0 && (
                <>
                  <TraceLabel>Входные данные</TraceLabel>
                  <TraceValue>{kpi.inputs.join('; ')}</TraceValue>
                </>
              )}
            </TraceList>
          </TraceCell>
        </tr>
      )}
    </>
  );
}

/** Блок «Показатели» — KPI-таблица, секционированная по группам показателя. */
function KpisSection({ kpis }: { kpis: ProgressKpi[] }) {
  if (kpis.length === 0) {
    return <Muted>Показатели появятся после завершения формирования отчёта.</Muted>;
  }
  return (
    <>
      {groupKpis(kpis).map(([group, rows]) => (
        <Fragment key={group}>
          <GroupCaption>{groupLabel(group)}</GroupCaption>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th scope="col">Показатель</th>
                  <th scope="col" className="num">
                    План
                  </th>
                  <th scope="col" className="num">
                    Факт
                  </th>
                  <th scope="col" className="num">
                    Δ
                  </th>
                  <th scope="col" className="num">
                    Δ%
                  </th>
                  <th scope="col">Ед.</th>
                  <th scope="col" aria-label="Статус" />
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => (
                  <KpiRow key={k.code} kpi={k} />
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Fragment>
      ))}
    </>
  );
}

/**
 * Роут `/agents/progress/runs/:id` — карточка сводного отчёта. Показывает живой
 * прогресс (пока активен), ленту ключевого освоения, KPI-таблицу из снимка (группы
 * + прослеживаемость + подсветка «требует проверки»), пояснительную записку,
 * предупреждения о нехватке данных, скачивание DOCX (по завершении) и HITL-решение
 * по отчёту (утвердить / отклонить / оспорить).
 */
export function ProgressRunDetail() {
  const { id } = useParams<{ id: string }>();
  const { can_write } = useProgressPermission();

  const [poll, setPoll] = useState(0);
  const { data: run, isLoading, isError } = useGetProgressRunQuery(id ?? '', {
    skip: !id,
    pollingInterval: poll,
  });

  const [decide, { isLoading: deciding }] = useDecideProgressRunMutation();
  const [exportDoc, { isFetching: downloading }] = useLazyExportProgressRunQuery();
  const [actionError, setActionError] = useState<string | null>(null);

  // Статус/прогресс обновляются асинхронно — пока отчёт активен, опрашиваем
  // карточку каждые 3с. На терминальном статусе poll выключаем.
  useEffect(() => {
    setPoll(run && isProgressRunActive(run.status) ? 3000 : 0);
  }, [run]);

  if (isLoading) {
    return (
      <Card>
        <Muted>Загрузка отчёта…</Muted>
      </Card>
    );
  }
  if (isError || !run) {
    return (
      <Card>
        <Muted>Не удалось загрузить отчёт. Возможно, он был удалён.</Muted>
      </Card>
    );
  }

  const snapshot = run.snapshot ?? null;
  const kpis = snapshot?.kpis ?? [];
  const deviations = snapshot?.deviations ?? [];
  const warnings = snapshot?.warnings ?? [];
  const decided = Boolean(run.decision);
  const title = run.summary?.trim() || runObjectLabel(run);

  async function handleDecision(decision: ProgressRunDecision) {
    if (!id) return;
    setActionError(null);
    try {
      await decide({ id, decision }).unwrap();
    } catch (err) {
      setActionError(apiErrorMessage(err) ?? 'Не удалось сохранить решение. Попробуйте ещё раз.');
      // eslint-disable-next-line no-console
      console.error('decideProgressRun failed', err);
    }
  }

  async function handleDownload() {
    if (!id) return;
    setActionError(null);
    try {
      const blob = await exportDoc(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `progress_report_${id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(apiErrorMessage(err) ?? 'Не удалось скачать отчёт (DOCX).');
      // eslint-disable-next-line no-console
      console.error('exportProgressRun failed', err);
    }
  }

  return (
    <Wrap>
      <Back to="/agents/progress/runs">
        <IconArrowLeft size={16} />
        <span>К списку отчётов</span>
      </Back>

      <HeaderCard>
        <HeadRow>
          <HeadLeft>
            <Eyebrow>Сводный отчёт по ходу строительства</Eyebrow>
            <RunTitle>{title}</RunTitle>
            <SubMeta>
              <Badge $tone={statusTone(run.status)}>{statusLabel(run.status)}</Badge>
              <span>создан {fmtDateTime(run.created_at)}</span>
              {snapshot && <span>· сформирован {fmtDateTime(snapshot.generated_at)}</span>}
              <span>· показателей: {kpis.length}</span>
            </SubMeta>
          </HeadLeft>
          <HeadActions>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={handleDownload}
              disabled={run.status !== 'completed' || downloading}
              title={
                run.status !== 'completed'
                  ? 'Доступно после завершения формирования'
                  : 'Скачать сводный отчёт в DOCX'
              }
            >
              <IconDownload size={14} />
              {downloading ? 'Готовлю…' : 'Скачать DOCX'}
            </Button>
          </HeadActions>
        </HeadRow>

        {isProgressRunActive(run.status) && (
          <ProgressBlock>
            <ProgressTopRow>
              <ProgressStage>{runStageLabel(run.status, run.progress_percent)}</ProgressStage>
              <ProgressPercent>{Math.round(run.progress_percent)}%</ProgressPercent>
            </ProgressTopRow>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(run.progress_percent)}
              aria-label={`Прогресс формирования: ${runStageLabel(run.status, run.progress_percent)}`}
            >
              <Progress percent={run.progress_percent} />
            </div>
          </ProgressBlock>
        )}

        {run.status === 'failed' && run.error_message && <ErrBox>{run.error_message}</ErrBox>}
        {actionError && <ErrBox>{actionError}</ErrBox>}
      </HeaderCard>

      {/* HITL-ревью отчёта: утвердить / отклонить / оспорить. */}
      {run.status === 'completed' && (
        <ReviewBar>
          {decided ? (
            <ReviewText>
              Решение по отчёту:{' '}
              <Badge $tone={decisionTone(run.decision ?? '')}>
                {decisionLabel(run.decision ?? '')}
              </Badge>
            </ReviewText>
          ) : (
            <ReviewText>
              Проверьте показатели и пояснительную записку, затем примите решение по отчёту.
            </ReviewText>
          )}
          {!decided && (
            <HeadActions>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => handleDecision('rejected')}
                disabled={!can_write || deciding}
                title={!can_write ? 'Только просмотр' : undefined}
              >
                Отклонить
              </Button>
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={() => handleDecision('disputed')}
                disabled={!can_write || deciding}
                title={!can_write ? 'Только просмотр' : undefined}
              >
                Оспорить
              </Button>
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                onClick={() => handleDecision('accepted')}
                disabled={!can_write || deciding}
                title={!can_write ? 'Только просмотр' : undefined}
              >
                {deciding ? 'Сохраняю…' : 'Утвердить'}
              </Button>
            </HeadActions>
          )}
        </ReviewBar>
      )}

      {/* Лента ключевого освоения (показатели с готовым процентом). */}
      {kpis.length > 0 && <HeadlineBand kpis={kpis} />}

      {/* Показатели (KPI) — по группам, с прослеживаемостью и подсветкой
          требующих проверки. */}
      <Card>
        <SectionTitle>
          Показатели <Badge $tone="neutral">{kpis.length}</Badge>
        </SectionTitle>
        <KpisSection kpis={kpis} />
      </Card>

      {/* Отклонения (Ф-3): посчитаны кодом, пояснение — осторожная гипотеза LLM
          (HITL). Показываем, когда детектор что-то нашёл. */}
      {deviations.length > 0 && (
        <Card>
          <SectionTitle>
            Отклонения <Badge $tone="warning">{deviations.length}</Badge>
          </SectionTitle>
          <DeviationsSection deviations={deviations} />
        </Card>
      )}

      {/* Пояснительная записка (текст LLM). Показываем, когда есть. */}
      {run.narrative?.trim() && (
        <NarrativeCard>
          <SectionTitle>Пояснительная записка</SectionTitle>
          <Narrative>{run.narrative}</Narrative>
        </NarrativeCard>
      )}

      {/* Предупреждения о нехватке данных. */}
      {run.status === 'completed' && (
        <Card>
          <SectionTitle>
            Предупреждения <Badge $tone="neutral">{warnings.length}</Badge>
          </SectionTitle>
          {warnings.length === 0 ? (
            <Muted>Предупреждений нет — данных по объекту достаточно для всех показателей.</Muted>
          ) : (
            <WarningList>
              {warnings.map((w, i) => (
                <WarningItem key={i}>
                  <Badge $tone="warning">нет данных</Badge>
                  <span>{w}</span>
                </WarningItem>
              ))}
            </WarningList>
          )}
        </Card>
      )}
    </Wrap>
  );
}
